import React, { useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, Keyboard, Switch, TouchableOpacity, FlatList } from 'react-native-web';
import { useRoute } from '@react-navigation/native';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import * as Animatable from 'react-native-animatable';
import moment from 'moment-timezone';
import { cloneDeep } from 'lodash';
import Select from 'react-select'

import './styles.css';

// Child Components
import SectionContainer from '../ProfileEdit/component_section_container';
import ActivitySearchModal from './component_activity_search_modal';
import BrewerySearchModal from './component_brewery_search_modal';
import AddressSearchModal from './component_address_search_modal';
import SquareLocationSelector from '../../components/component_square_location_selector';

// Custom Components
import { Button, InScreenModal, NavBar, TextBox } from '../../components';
import LoadingView from '../../components/component_loading_view';

// Utils
import { getDefaultSquareLocation } from '../../utils/util_main_app';
import { getConvertedTimezone } from '../../utils/util_date_checker';
import { roundNumberToDecimal, showAlert, showAppError } from '../../utils/util_helpers';
import { isRecurringEvent } from '../../utils/util_events';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';

// APIs
import { toCurrentDeviceTime } from '../../modules/converters';
import { DatePickerModal, TimePickerModal } from 'react-native-paper-dates';
import { SalesTaxSelector } from '../../components/component_sales_tax_selector';
import { exportSingleCalendarEvent, editEvent, deleteCalendarEvent } from '../../modules';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import { toast } from 'react-hot-toast';
import { promptRemoveEvent } from '../../utils/util_events';

// Animatable Components
const AnimatableScrollView = Animatable.createAnimatableComponent(ScrollView);

export function ProfileScheduleEdit() {
    const {
        eventData,
        onEventCreated = () => {},
        onEventUpdated = () => {},
        onEventRemoved = () => {}
    } = useRoute()?.params || {};

    const EDIT_MODE = !!eventData?.id;

    // region Google Places
    const placesService = {}, placePredictions = [], getPlacePredictions = () => {};
    // TODO: usePlacesService({ apiKey: process.env.REACT_APP_GOOGLE_API_KEY });
    // endregion

    // region Refs
    const loadingInitialPrediction = useRef(true);
    const activityModal = useRef(null);
    const breweryModal = useRef(null);
    const addressModal = useRef(null);
    const squareLocationSelector = useRef(null);
    const salesTaxSelector = useRef(null);
    // endregion

    // region Global State
    const [squareLocationList] = useGlobalState(STATE_KEYS.LOCATION_LIST);
    const [squareLocationIndex] = useGlobalState(STATE_KEYS.CURRENT_LOCATION_INDEX);
    const [globalTruckData, setGlobalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    // endregion

    // region Local State
    const [compShouldLoad, setCompShouldLoad] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [isDeleting, setDeleting] = useState(false);

    const [showStartDatePicker, setShowStartDatePicker] = useState(false);
    const [showStartTimePicker, setShowStartTimePicker] = useState(false);
    const [showEndDatePicker, setShowEndDatePicker] = useState(false);
    const [showEndTimePicker, setShowEndTimePicker] = useState(false);

    const [name, setName] = useState('');
    const [startTime, setStartTime] = useState(new Date());
    const [endTime, setEndTime] = useState(new Date());

    const [selectedSquareLocationIndex, setSelectedSquareLocationIndex] = useState(
        eventData?.squareup_location_id ?
            squareLocationList.length > 0 ? (eventData.id ? squareLocationList.findIndex((item) => item.squareup_location_id === eventData.squareup_location_id) : squareLocationIndex) : -1
            :
            getDefaultSquareLocation().itemIndex,
    );

    const [showRepeatModal, setShowRepeatModal] = useState(false);
    const [tempSelectedPresetIndex, setTempSelectedPresetIndex] = useState(0);
    const [currentRepeatString, setCurrentRepeatString] = useState('');
    const [selectedFrequency, setSelectedFrequency] = useState('once');
    const [selectedFrequencyID, setSelectedFrequencyID] = useState(3);
    const [selectedRepeatTimes, setSelectedRepeatTimes] = useState(0);
    const [selectedInterval, setSelectedInterval] = useState(1);
    const [selectedRepeatIndex, setSelectedRepeatIndex] = useState(0);

    const [tempInterval, setTempInterval] = useState(1);
    const [tempFrequencyName, setTempFrequencyName] = useState({ label: 'once', value: 'ONCE' });
    const [tempRepeatTimes, setTempRepeatTimes] = useState(0);

    const [latitude, setLatitude] = useState(-1);
    const [longitude, setLongitude] = useState(-1);
    const [address, setAddress] = useState('');
    const [breweryData, setBreweryData] = useState(null);
    const [activityData, setActivityData] = useState(null);

    const [timezone, setTimezone] = useState('America/Denver');

    const [customSalesTax, setCustomSalesTax] = useState(null);

    const [allowPreOrdering, setAllowPreOrdering] = useState(false);
    // endregion

    // region Repeat Preset
    const REPEAT_PRESET = [
        {
            currentRepeatString: 'Does not repeat',
            selectedFrequency: 'ONCE',
            selectedFrequencyID: 3,
            selectedTimesRepeated: null,
            selectedInterval: 1,
            pickerData: { label: 'once', value: 'ONCE' }
        },
        {
            currentRepeatString: 'Every day',
            selectedFrequency: 'DAILY',
            selectedFrequencyID: 4,
            selectedTimesRepeated: null,
            selectedInterval: 1,
            pickerData: { label: 'day', value: 'DAILY' }
        },
        {
            currentRepeatString: 'Every week',
            selectedFrequency: 'WEEKLY',
            selectedFrequencyID: 2,
            selectedTimesRepeated: null,
            selectedInterval: 1,
            pickerData: { label: 'week', value: 'WEEKLY' }
        },
        {
            currentRepeatString: 'Every month',
            selectedFrequency: 'MONTHLY',
            selectedFrequencyID: 1,
            selectedTimesRepeated: null,
            selectedInterval: 1,
            pickerData: { label: 'month', value: 'MONTHLY' }
        },
        {
            currentRepeatString: 'Custom...'
        }
    ];
    // endregion

    // region TAX LIST
    const TAX_LIST = [{ id: -1, name: 'Use Location\'s Default' }, { id: 0, name: 'No Sales Tax' }].concat(Object.values(GLOBAL.DATA.SALES_TAX_OPTIONS));
    // endregion

    // region Component Mounting
    useEffect(() => {
        if (eventData) {
            setTimeout(() => initializeData(), 1000);
        }
    }, []);
    // endregion

    // region Main Func
    const mainFunc = (customEventItem = null) => {
        const eventItem = cloneDeep(customEventItem || eventData || {});

        const presetObj = getPresetObj(eventItem['frequency'] ? eventItem['frequency'].name : 'ONCE');
        const timezone = getConvertedTimezone(eventItem['timezone']);

        setName(eventItem['location_name'] || '');
        setStartTime((eventItem['start_time'] ? toCurrentDeviceTime(eventItem['start_time'], timezone) : (eventItem['timeObj'] ? moment.unix(eventItem['timeObj'].startTime) : moment())).toDate());
        setEndTime(
            (
                eventItem['end_time'] ?
                    toCurrentDeviceTime(eventItem['end_time'], timezone)
                    :
                    (
                        eventItem['timeObj'] ?
                            (
                                eventItem['timeObj']?.endTime ?
                                    moment.unix(eventItem['timeObj'].endTime)
                                    :
                                    moment.unix(eventItem['timeObj'].startTime).add(1, 'h')
                            )
                            :
                            moment().add(1, 'h')
                    )
            ).toDate()
        );
        setTimezone(timezone);

        const initialRepeatTimes = (eventItem['times_repeated'] || 0) + 1;
        const initialInterval = eventItem['interval'] || 1;
        const initialFrequency = presetObj.selectedFrequency;
        setTempSelectedPresetIndex(getSelectedRepeatIndex(getCurrentRepeatString(initialRepeatTimes, initialInterval, initialFrequency)));
        setTempFrequencyName(REPEAT_PRESET.find((item) => item.selectedFrequency === initialFrequency)?.pickerData);
        setTempInterval(initialInterval);
        setTempRepeatTimes(initialRepeatTimes);

        setSelectedFrequency(presetObj.selectedFrequency);
        setSelectedFrequencyID(presetObj.selectedFrequencyID);
        setSelectedRepeatTimes(initialRepeatTimes);
        setSelectedInterval(eventItem['interval'] || 1);

        setLatitude(Number(eventItem['lat'] || eventItem['latitude'] || -1));
        setLongitude(Number(eventItem['long'] || eventItem['longtitude'] || -1));
        setAddress(eventItem['address'] || 'Denver, Colorado');
        setBreweryData(eventItem['brewery'] || eventItem['breweries'] || null);
        setActivityData(eventItem['activity'] || eventItem['activities'] || null);

        setAllowPreOrdering(!eventItem?.id ? (Number(globalTruckData['pre_ordering']) === 1) : (eventItem['pre_ordering'] === 1));
        setCustomSalesTax(!eventItem?.id ? null : (eventItem['tax_id'] === 0 ? 0 : (!eventItem['tax_id'] ? null : eventItem['tax'])));

        setTimeout(() => {
            setCompShouldLoad(true);
            setTimeout(() => {
                const repeatStr = getCurrentRepeatString();
                setCurrentRepeatString(repeatStr);
                setSelectedRepeatIndex(getSelectedRepeatIndex(repeatStr));
            }, 1000);
        }, 500);
    }
    // endregion

    // region Function - Initialize Data
    const initializeData = () => {
        // (Single Import Event) Try to get coordinates of the event if address exists
        // if (eventData.address?.length && !eventData.id) {
        //     getPlacePredictions({ input: eventData.address });
        // }
        // else mainFunc();
        mainFunc();
    };
    // endregion

    // region Watcher - Initial Place Prediction
    useEffect(() => {
        if (loadingInitialPrediction.current) {
            console.log('[INFO] RN GOOGLE PLACES PREDICTION', placePredictions);
            if (placePredictions.length > 0) {
                const eventItem = cloneDeep(eventData);

                // Just take the first result and then call another API to get the coordinates
                placesService?.getDetails({ placeId: placePredictions[0]?.['place_id'] }, (placeDetails) => {
                    console.log('[INFO] RN GOOGLE PLACES LOOK UP BY ID', placeDetails);
                    eventItem['lat'] = roundNumberToDecimal(placeDetails.location?.latitude || -1, 6);
                    eventItem['long'] = roundNumberToDecimal(placeDetails.location?.longitude || -1, 6);
                    console.log('[INFO] NEW ITEM DATA WITH COORDINATES', eventItem);
                    mainFunc(eventItem);
                });
            }
            else mainFunc();
            loadingInitialPrediction.current = false;
        }
    }, [placePredictions, loadingInitialPrediction]);
    // endregion

    // region Function - Go Add Event
    const goSaveEvent = () => {
        Keyboard.dismiss();
        const isAdding = !EDIT_MODE;

        if (isLoading) return;

        // Check if event name is not entered
        if (!name.trim()) {
            showAlert({ text: 'Please enter your event name' });
            return;
        }

        // Check if event location is not selected
        if ((Number(latitude) === -1) || (Number(latitude) === 0)) {
            showAlert({ text: 'Please select a location where your event is happening' });
            return;
        }

        const eventItem = cloneDeep(eventData || {});

        if (!eventItem['timeObj']) eventItem['timeObj'] = {};

        eventItem['timeObj'].startTime = moment(startTime).unix();
        eventItem['timeObj'].endTime = moment(endTime).unix();
        eventItem[isAdding ? 'lat' : 'latitude'] = latitude;
        eventItem[isAdding ? 'long' : 'longtitude'] = longitude;
        eventItem['address'] = address;
        eventItem['location_name'] = name;

        if (breweryData) {
            eventItem['brewery_id'] = breweryData.id;
            eventItem['address'] = breweryData.location;
            eventItem[isAdding ? 'brewery' : 'breweries'] = breweryData;

            ['activity_id', 'activity', 'activities', 'activities_id'].forEach((key) => {
                if (eventItem.hasOwnProperty(key)) delete eventItem[key];
            });
        }
        else if (activityData) {
            eventItem['activity_id'] = activityData.id;
            eventItem['activities_id'] = activityData.id;
            eventItem['activity'] = activityData;
            eventItem['activities'] = activityData;
            eventItem['address'] = activityData.address;

            ['brewery_id', 'breweries_id', 'breweries', 'brewery'].forEach((key) => {
                if (eventItem.hasOwnProperty(key)) delete eventItem[key];
            });
        }
        else {
            ['brewery_id', 'breweries_id', 'breweries', 'brewery', 'activity_id', 'activity', 'activities', 'activities_id'].forEach((key) => {
                if (eventItem.hasOwnProperty(key)) delete eventItem[key];
            });
        }

        // Convert to UTC Time
        eventItem['start_time'] = moment(startTime).utc().format('YYYY-MM-DD HH:mm:ss');
        eventItem['end_time'] = moment(endTime).utc().format('YYYY-MM-DD HH:mm:ss');

        // Check frequency
        eventItem['frequency'] = selectedFrequency;
        eventItem['frequency_id'] = selectedFrequencyID;

        // Check interval
        eventItem['interval'] = selectedInterval;

        // Check time repeated
        eventItem['times_repeated'] = selectedRepeatTimes - 1;

        // Change truck name
        eventItem['food_truck'] = globalTruckData.name;

        // Pre-Ordering
        eventItem['pre_ordering'] = (allowPreOrdering ? 1 : 0);

        // Custom sales tax
        eventItem['tax_id'] = customSalesTax !== 0 ? (customSalesTax?.id || customSalesTax) : 0;

        // Remove redundant data to avoid conflict
        if (isAdding) {
            // Remove redundant data
            ['id', 'timeObj', 'brewery'].forEach((key) => {
                if (eventItem.hasOwnProperty(key)) delete eventItem[key];
            });

            // Check fields from Google Calendar imports
            if (!eventItem['last_start_time']) eventItem['last_start_time'] = null;
            if (!eventItem['last_end_time']) eventItem['last_end_time'] = null;
            if (!eventItem['recurring_every']) eventItem['recurring_every'] = null;
            if (!eventItem['featured_pairings']) eventItem['featured_pairings'] = null;
            if (!eventItem['notes']) eventItem['notes'] = '';
        }

        console.log(`[INFO] SENDING DATA (${isAdding ? 'ADD' : 'EDIT'} EVENT)`, eventItem);

        // Start calling API to export event
        setLoading(true);
        const squareLocationIndex = selectedSquareLocationIndex > -1 ? selectedSquareLocationIndex : 0;
        const squareLocationID = squareLocationList.length > 0 ? squareLocationList[squareLocationIndex].squareup_location_id : null;
        const apiCall = isAdding ? exportSingleCalendarEvent(eventItem, squareLocationID) : editEvent(eventItem, squareLocationID);

        apiCall
            .then((result) => {
                if (isAdding) {
                    // Callback
                    if (!!onEventCreated) {
                        onEventCreated(Object.assign(cloneDeep(result), customSalesTax !== null ? { tax: customSalesTax } : { tax: squareLocationList[squareLocationIndex]?.tax }));
                        RootNavigation.pop();
                    }
                    else RootNavigation.pop();
                }
                else {
                    // Callback
                    if (!!onEventUpdated) {
                        const updatedItem = Object.assign({},
                            cloneDeep(eventData),
                            result,
                            customSalesTax !== null ? { tax: customSalesTax } : { tax: squareLocationList[squareLocationIndex]?.tax },
                            activityData ? { activity: activityData } : { activity: null },
                            breweryData ? { brewery: breweryData } : { brewery: null },
                        );

                        onEventUpdated(eventData, updatedItem);
                        RootNavigation.pop();
                    }
                    else RootNavigation.pop();
                }
            })
            .catch((error) => showAppError(error))
            .finally(() => setLoading(false));
    }
    // endregion

    // region Function - Delete Event
    const goDeleteEvent = () => {
        promptRemoveEvent({
            eventData,
            onDeletePressed: () => setDeleting(true),
            onDeleteCompleted: (newEventResult = {}, type = 3) => {
                setDeleting(false);

                // Generate new calendar obj
                const newCalendarArr = cloneDeep(globalTruckData.calendar);
                const eventIndex = newCalendarArr.findIndex((item) => item.id === eventData.id);
                if (type === 3) {
                    newCalendarArr.splice(eventIndex, 1);
                }
                else newCalendarArr.splice(eventIndex, 1, newEventResult);

                // Update Profile tab state
                const newTruckData = Object.assign(cloneDeep(globalTruckData), { calendar: newCalendarArr });
                onEventRemoved(newTruckData);

                // Close Screen
                RootNavigation.pop();

                // Notify
                toast('Event removed from your calendar');
            },
            onDeleteError: (error) => {
                setDeleting(false);
                showAppError(error);
            }
        });
    }
    // endregion

    // region Function - Get Selected Occurrence Index
    const getSelectedRepeatIndex = (repeatString) => {
        const presetIndex = REPEAT_PRESET.findIndex((item) => item.currentRepeatString.toUpperCase() === repeatString.toUpperCase());
        return(presetIndex > -1 ? presetIndex : (REPEAT_PRESET.length - 1));
    }
    // endregion

    // region Function - Get Preset Data
    const getPresetObj = (inputString) => {
        const presetObj = REPEAT_PRESET.find((item) => (item.currentRepeatString.toLowerCase() === inputString.toLowerCase()) || (item.selectedFrequency.toLowerCase() === inputString.toLowerCase()));
        return(presetObj || REPEAT_PRESET[REPEAT_PRESET.length - 1]);
    }
    // endregion

    // region Function - Get Current Repeat String
    const getCurrentRepeatString = (repeatTimes = selectedRepeatTimes, interval = selectedInterval, frequency = selectedFrequency) => {
        const repeatString = `Every${interval > 1 ? ' ' + interval : ''} ${frequency.toUpperCase() !== 'DAILY' ? frequency.substring(0, frequency.length - 2).toLowerCase() : 'day'}${interval > 1 ? 's' : ''}` + (repeatTimes ? `, ${repeatTimes} time${repeatTimes > 1 ? 's' : ''}` : '');
        return(frequency === 'ONCE' ? 'Does not repeat' : repeatString);
    }
    // endregion

    // region Function - Get Frequency ID
    const getFrequencyID = (frequencyString) => {
        const presetObj = REPEAT_PRESET.find((item) => item.selectedFrequency.toUpperCase() === frequencyString.toUpperCase());
        return(presetObj ? presetObj.selectedFrequencyID : REPEAT_PRESET[0].selectedFrequencyID);
    }
    // endregion

    // region Render Time Input
    const renderTimeInput = ({ title = '', value = '', onPress = () => {}, isLeft = false }) => (
        <TextBox
            title={title}
            value={value}
            onPress={onPress}
            customOnPressIcon={'event'}
            style={[{ flex: 1, marginTop: 0 }, isLeft ? { marginRight: 4 } : { marginLeft: 4 }]}
        />
    )
    // endregion

    // region Repeat Item
    const renderRepeatItem = ({ item, index }) => {
        const indicatorSize = 24;
        const isSelected = tempSelectedPresetIndex === index;
        const customSelected = ((item.currentRepeatString === 'Custom...') && isSelected);
        return(
            <TouchableOpacity
                key={item.currentRepeatString}
                onPress={() => setTempSelectedPresetIndex(index)}
                style={{
                    alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', padding: 24,
                    borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER, borderRadius: 8, marginBottom: 16
                }}>
                {/* Selection Indicator */}
                <View style={{
                    width: indicatorSize, height: indicatorSize,
                    borderRadius: indicatorSize / 2,
                    borderWidth: 2, borderColor: isSelected ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.TEXT_LIGHT_GRAY,
                    backgroundColor: isSelected ? GLOBAL.COLOR.MAIN : 'white',
                    alignItems: 'center', justifyContent: 'center'
                }}>
                    {isSelected && <View style={{ width: 8, height: 8, backgroundColor: 'white', borderRadius: 4 }}/>}
                </View>

                {/* Name */}
                <Text style={{ fontWeight: customSelected ? 'bold' : '500', fontSize: 18, flex: 1, marginLeft: 16, color: customSelected ? GLOBAL.COLOR.BASIC.UI_BLUE : GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                    {
                        item.currentRepeatString !== 'Custom...' ?
                            item.currentRepeatString
                            :
                            ((getSelectedRepeatIndex(getCurrentRepeatString()) < (REPEAT_PRESET.length - 1)) ? 'Custom...' : 'Custom: ' + getCurrentRepeatString())
                    }
                </Text>
            </TouchableOpacity>
        );
    }
    // endregion

    // region Render Repeat Modal
    const renderRepeatModal = () => (
        <InScreenModal visible={showRepeatModal}>
            {/* Nav Bar */}
            <NavBar
                title={'Occurrence Schedule'}
                leftButtonIconName={'close'}
                leftButtonOnPress={() => {
                    // Reset temp selections
                    setTempSelectedPresetIndex(selectedRepeatIndex);
                    setTempRepeatTimes(selectedRepeatTimes);
                    setTempFrequencyName(REPEAT_PRESET.find((item) => item.selectedFrequency === selectedFrequency)?.pickerData);
                    setTempInterval(selectedInterval);

                    // Close modal
                    setShowRepeatModal(false);
                }}
                rightButtonTitle={'Save'}
                rightButtonOnPress={() => {
                    const item = REPEAT_PRESET[tempSelectedPresetIndex];
                    setSelectedRepeatIndex(tempSelectedPresetIndex);
                    if (tempSelectedPresetIndex !== (REPEAT_PRESET.length - 1)) {
                        setCurrentRepeatString(item.currentRepeatString);
                        setSelectedFrequency(item.selectedFrequency);
                        setSelectedFrequencyID(item.selectedFrequencyID);
                        setSelectedInterval(item.selectedInterval);
                    }
                    else {
                        setCurrentRepeatString(getCurrentRepeatString(tempRepeatTimes, tempInterval, tempFrequencyName?.value));
                        setSelectedFrequency(tempFrequencyName?.value);
                        setSelectedFrequencyID(getFrequencyID(tempFrequencyName?.value));
                        setSelectedInterval(tempInterval);
                        setSelectedRepeatTimes(tempRepeatTimes);
                    }
                    setShowRepeatModal(false);
                }}
            />

            {/* Preset List */}
            <ScrollView
                style={{ flex: 1, width: '100%' }}
                contentContainerStyle={{ padding: 24 }}
                showsVerticalScrollingIndicator={false}>
                {/* Preset */}
                {REPEAT_PRESET.map((item, index) => renderRepeatItem({ item, index }))}

                {/* Custom Options */}
                {
                    (tempSelectedPresetIndex === (REPEAT_PRESET.length - 1)) &&
                    <View style={{ width: '100%', paddingLeft: 64 }}>
                        {/* Frequency */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch' }}>
                            <TextBox
                                title={'Repeat every'}
                                placeholder={'Repeat every...'}
                                value={(tempFrequencyName.value === 'ONCE') ? '0' : tempInterval?.toString()}
                                onChangeText={(text) => setTempInterval(Number(text))}
                                showError={isNaN(tempInterval)}
                                style={{ marginTop: 0 }}
                                disabled={(tempFrequencyName.value === 'ONCE')}
                            />

                            <Select
                                className={'selectComp'}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderWidth: 2,
                                        borderRadius: 6
                                    })
                                }}
                                options={[
                                    { label: 'once', value: 'ONCE' },
                                    { label: 'day', value: 'DAILY' },
                                    { label: 'week', value: 'WEEKLY' },
                                    { label: 'month', value: 'MONTHLY' }
                                ]}
                                value={tempFrequencyName}
                                onChange={(item) => {
                                    setTempFrequencyName(item);
                                    if (item.label !== 'once' && !tempRepeatTimes) setTempRepeatTimes(1);
                                }}
                                isSearchable={false}
                                isClearable={false}
                            />

                            {/* Occurrences */}
                            {
                                (tempFrequencyName.value !== 'ONCE') &&
                                <>
                                    <Text style={{ marginHorizontal: 16 }}>
                                        and
                                    </Text>

                                    <TextBox
                                        title={'ends after'}
                                        placeholder={'ends after...'}
                                        value={tempRepeatTimes?.toString() || '0'}
                                        onChangeText={(text) => setTempRepeatTimes(Number(text))}
                                        style={{ marginTop: 0, marginLeft: 0, width: 150 }}
                                    />

                                    <Text style={{ marginLeft: 14 }}>
                                        {tempRepeatTimes ? 'occurrences' : '(does not end)'}
                                    </Text>
                                </>
                            }
                        </View>
                    </View>
                }
            </ScrollView>
        </InScreenModal>
    )
    // endregion

    // region Render Time Section
    const renderTimeSection = () => {
        const isRecurring = isRecurringEvent(eventData);
        const timeFormat = 'hh:mm A | dddd, MMM Do, YYYY';
        return(
            <>
                {/* Recurring Message */}
                {
                    isRecurring &&
                    <Text style={{ color: GLOBAL.COLOR.BASIC.RED, fontSize: 16, lineHeight: 26, alignSelf: 'stretch', marginVertical: 24 }}>
                        This is a recurring event, the following shows original event's date and time, editing will affect all occurrences of this event.
                    </Text>
                }

                {/* Start Time / End Time */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginTop: isRecurring ? 0 : 16 }}>
                    {/* Start Time */}
                    {renderTimeInput({
                        value: moment(startTime).format(timeFormat),
                        title: 'Start Time',
                        isLeft: true,
                        onPress: () => setShowStartDatePicker(true)
                    })}

                    {/* Start Date Time Picker */}
                    {renderStartTimePicker()}

                    {/* End Time */}
                    {renderTimeInput({
                        value: moment(endTime).format(timeFormat),
                        title: 'End Time',
                        isLeft: false,
                        onPress: () => setShowEndDatePicker(true)
                    })}

                    {/* End Date Time Picker */}
                    {renderEndTimePicker()}
                </View>

                {/* Recurring */}
                <TextBox
                    title={'Repeat'}
                    value={getCurrentRepeatString()}
                    onPress={() => setShowRepeatModal(true)}
                    customOnPressIcon={'event-repeat'}
                    style={{ flex: 1 }}
                />
            </>
        );
    }
    // endregion

    // region Render Square Location
    const renderSquareLocationSection = () => (
        !!squareLocationList.length &&
        <SectionContainer
            embeddedMode={true}
            style={{ marginTop: 36 }}
            title={'Square Location'}
            description={'When customers order from this event, the orders will go to the selected location.'}>
            <TextBox
                title={'Selected Square Location'}
                value={squareLocationList[selectedSquareLocationIndex > -1 ? selectedSquareLocationIndex : 0].name}
                onPress={() => squareLocationSelector.current?.showModal()}
                customOnPressIcon={require('../../images/square/square@2x.png')}
                style={{ flex: 1, marginTop: 0 }}
            />
        </SectionContainer>
    )
    // endregion

    // region Render Custom Sales Tax
    const renderCustomSalesTaxSection = () => {
        if (!squareLocationList.length) return false;
        const selectedTax = (customSalesTax !== null && customSalesTax !== 0) ? GLOBAL.DATA.SALES_TAX_OPTIONS[customSalesTax.id] : customSalesTax;
        const selectedSquareLocation = squareLocationList[selectedSquareLocationIndex];
        return(
            !!squareLocationList.length &&
            <SectionContainer
                embeddedMode={true}
                style={{ marginTop: 36 }}
                title={'Custom Sales Tax'}
                description={'Choose your sales tax for this event. This tax rate will override your location sales tax in the order settings.'}>
                <TextBox
                    title={'Selected Sales Tax'}
                    value={
                        customSalesTax !== null ?
                            (customSalesTax === 0 ? '(No Sales Tax)' : `${selectedTax?.name} (${Number(selectedTax?.value).toFixed(2)}%)`)
                            :
                            (
                                selectedSquareLocation?.tax ?
                                    `${selectedSquareLocation?.tax?.name} (${Number(selectedSquareLocation?.tax?.value).toFixed(2)}%)`
                                    :
                                    '(No Sales Tax)'
                            )
                    }
                    onPress={() => salesTaxSelector.current?.showModal()}
                    customOnPressIcon={'paid'}
                    style={{ flex: 1, marginTop: 0 }}
                />
            </SectionContainer>
        );
    }
    // endregion

    // region Render Event Address
    const renderEventAddressSection = () => (
        <SectionContainer
            embeddedMode={true}
            style={{ marginTop: 36 }}
            title={'Event Location'}
            description={'Select location where your event will be at.'}>
            <TextBox
                title={(activityData || breweryData) ? (activityData?.name || breweryData?.name) : 'Selected Address'}
                value={
                    (activityData || breweryData) ?
                        activityData?.address || (breweryData?.location || eventData?.address)
                        :
                        (latitude !== -1 ? address : 'Tap to select event location')
                }
                onPress={() => {
                    showAlert({
                        text: 'How do you want to search for the event location?',
                        cancelButton: {
                            text: 'Enter Address',
                            onPress: () => addressModal.current?.showModal()
                        },
                        confirmButton: {
                            text: 'Brewery',
                            onPress: () => breweryModal.current?.showModal()
                        },
                        thirdButton: {
                            text: 'Public Activity',
                            onPress: () => activityModal.current?.showModal()
                        }
                    })
                }}
                customOnPressIcon={activityData ? 'park' : (breweryData ? 'sports-bar' : 'location-on')}
                style={{ flex: 1, marginTop: 0 }}
            />
        </SectionContainer>
    )
    // endregion

    // region Render Pre-Ordering Section
    const renderPreOrderingSection = () => (
        <View style={{ alignSelf: 'stretch', marginTop: 36 }}>
            {/* Title */}
            <Text style={{ fontSize: 24, fontWeight: 'bold', color: 'black' }}>
                Pre-Ordering
            </Text>

            {/* Description */}
            <View style={{ width: '100%', flexDirection: 'row', alignItems: 'flex-start' }}>
                {/* Text */}
                <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, fontSize: 16, fontWeight: 'normal', marginTop: 12, flex: 1 }}>
                    By turning this on, pre-ordering will be available 72 hours before this event starts.
                </Text>

                {/* Switch */}
                <Switch
                    value={allowPreOrdering}
                    trackColor={'#DEDEDE'}
                    activeTrackColor={GLOBAL.COLOR.BASIC.UI_GREEN}
                    activeThumbColor={'white'}
                    thumbColor={'white'}
                    onValueChange={(switchValue) => setAllowPreOrdering(switchValue)}
                />
            </View>
        </View>
    )
    // endregion

    // region Render Start Time Picker
    const renderStartTimePicker = () => (
        <>
            <DatePickerModal
                locale={'en'}
                mode={'single'}
                visible={showStartDatePicker}
                date={startTime}
                saveLabelDisabled={true}
                validRange={{ startDate: moment().startOf('day').toDate() }}
                onChange={({ date }) => {
                    const newDate = moment(startTime).clone();
                    newDate.set('date', date.getDate());
                    newDate.set('month', date.getMonth());
                    newDate.set('year', date.getFullYear());
                    setStartTime(newDate.toDate());
                    setShowStartDatePicker(false);
                    setShowStartTimePicker(true);
                }}
                onDismiss={() => setShowStartDatePicker(false)}
            />

            <TimePickerModal
                locale={'en'}
                mode={'single'}
                defaultInputType={'keyboard'}
                visible={showStartTimePicker}
                hours={moment(startTime).hours()}
                minutes={moment(startTime).minutes()}
                use24HourClock={false}
                onConfirm={({ hours, minutes }) => {
                    const newDate = moment(startTime).clone();
                    newDate.set('minutes', minutes);
                    newDate.set('hours', hours);
                    setStartTime(newDate.toDate());
                    setShowStartTimePicker(false);

                    // If start time is after end time
                    if (newDate.isAfter(moment(endTime))) {
                        setEndTime(newDate.clone().add(1, 'hour').toDate());
                    }
                }}
                onDismiss={() => setShowStartTimePicker(false)}
            />
        </>
    );
    // endregion

    // region Render End Time Picker
    const renderEndTimePicker = () => (
        <>
            <DatePickerModal
                locale={'en'}
                mode={'single'}
                visible={showEndDatePicker}
                date={endTime}
                saveLabelDisabled={true}
                onChange={({ date }) => {
                    const newDate = moment(endTime).clone();
                    newDate.set('date', date.getDate());
                    newDate.set('month', date.getMonth());
                    newDate.set('year', date.getFullYear());
                    setEndTime(newDate.toDate());
                    setShowEndDatePicker(false);
                    setShowEndTimePicker(true);
                }}
                onDismiss={() => setShowEndDatePicker(false)}
            />

            <TimePickerModal
                locale={'en'}
                mode={'single'}
                defaultInputType={'keyboard'}
                visible={showEndTimePicker}
                hours={moment(endTime).hours()}
                minutes={moment(endTime).minutes()}
                use24HourClock={false}
                onConfirm={({ hours, minutes }) => {
                    const newDate = moment(endTime).clone();
                    newDate.set('minutes', minutes);
                    newDate.set('hours', hours);
                    setEndTime(newDate.toDate());
                    setShowEndTimePicker(false);

                    // If end time is before start time
                    if (newDate.isBefore(moment(startTime))) {
                        setStartTime(newDate.clone().subtract(1, 'hour').toDate());
                    }
                }}
                onDismiss={() => setShowEndTimePicker(false)}
            />
        </>
    );
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ width: '100%', flex: 1 }}>
            {/* Nav Bar */}
            <NavBar
                title={EDIT_MODE ? 'Edit Event' : 'New Event'}
                rightButtonTitle={'Save'}
                rightButtonOnPress={() => {
                    showAlert({
                        text: `Confirm ${EDIT_MODE ? 'updating' : 'creating new'} event?`,
                        cancelButton: { text: 'Cancel' },
                        confirmButton: {
                            text: 'Confirm',
                            color: GLOBAL.COLOR.MAIN,
                            onPress: () => goSaveEvent()
                        }
                    })
                }}
            />

            {/* Main View */}
            {
                compShouldLoad ?
                    <AnimatableScrollView
                        animation={'fadeIn'}
                        style={{ flex: 1, width: '100%' }}
                        contentContainerStyle={{ paddingBottom: 24, alignItems: 'center', paddingHorizontal: '15%' }}>
                        {/* Main Content */}
                        <View style={{ alignSelf: 'stretch', backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, padding: 24, marginTop: 24 }}>
                            {/* Name Input */}
                            <TextBox
                                title={'Event Name'}
                                placeholder={'Event Name'}
                                returnKeyType={'next'}
                                autoCapitalize={'words'}
                                autoCorrect={false}
                                textInputStyle={{ fontWeight: 'bold' }}
                                containerStyle={{ alignSelf: 'stretch' }}
                                style={{ marginTop: 0 }}
                                onSubmitEditing={() => Keyboard.dismiss()}
                                onChangeText={setName}
                                value={name}
                            />

                            {/* Time Section */}
                            {renderTimeSection()}

                            {/* Square Selector */}
                            {renderSquareLocationSection()}

                            {/* Sales Tax */}
                            {renderCustomSalesTaxSection()}

                            {/* Event Location */}
                            {renderEventAddressSection()}

                            {/* Pre-Ordering */}
                            {renderPreOrderingSection()}
                        </View>

                        {/* Delete Button */}
                        {
                            EDIT_MODE &&
                            <Button
                                text={'Delete Event'}
                                textColor={'white'}
                                onPress={() => goDeleteEvent()}
                                style={{ alignSelf: 'stretch', backgroundColor: GLOBAL.COLOR.MAIN, marginTop: 24 }}
                                isLoading={isDeleting}
                                loadingText={'Deleting Event'}
                            />
                        }
                    </AnimatableScrollView>
                    :
                    <LoadingView isLoading={true} />
            }

            {/* Activity Search Modal */}
            <ActivitySearchModal
                ref={activityModal}
                onActivitySelected={({ activityData: newActivity, address: newAddress, latitude: newLat, longitude: newLong }) => {
                    setBreweryData(null);
                    setActivityData(newActivity);
                    setAddress(newAddress);
                    setLatitude(newLat);
                    setLongitude(newLong);
                }}
            />

            {/* Brewery Search Modal */}
            <BrewerySearchModal
                ref={breweryModal}
                onBrewerySelected={({ breweryData: newBrewery, address: newAddress, latitude: newLat, longitude: newLong }) => {
                    setActivityData(null);
                    setBreweryData(newBrewery);
                    setAddress(newAddress);
                    setLatitude(newLat);
                    setLongitude(newLong);
                }}
            />

            {/* Custom Address Modal */}
            <AddressSearchModal
                ref={addressModal}
                onPlaceSelected={(place) => {
                    setBreweryData(null);
                    setActivityData(null);
                    setAddress(place.formatted_address);
                    setLatitude(place['geometry'].location.lat());
                    setLongitude(place['geometry'].location.lng());
                }}
            />

            {/* Square Location Selector */}
            <SquareLocationSelector
                ref={squareLocationSelector}
                showNote={false}
                buttonText={'Select Location'}
                locationList={squareLocationList}
                currentLocationIndex={selectedSquareLocationIndex}
                onLocationSelected={(index) => {
                    if (index !== selectedSquareLocationIndex) {
                        setSelectedSquareLocationIndex(index);
                        setCustomSalesTax(null);
                    }
                }}
            />

            {/* Custom Sales Tax Selector */}
            <SalesTaxSelector
                ref={salesTaxSelector}
                taxList={TAX_LIST}
                onTaxItemSelected={(selectedTax) => {
                    if (selectedTax.id === -1) {
                        setCustomSalesTax(null);
                    }
                    else if (selectedTax.id === 0) {
                        setCustomSalesTax(0);
                    }
                    else setCustomSalesTax(selectedTax);
                }}
            />

            {/* Repeat Modal */}
            {renderRepeatModal()}

            {/* Loading View */}
            {(isLoading || isDeleting) && <LoadingView isAbsolute={true} description={isDeleting ? 'Deleting event' : 'Saving event'}/>}
        </View>
    );
    // endregion
}
